/**
 * Conditional Logic Utilities
 *
 * Evaluates conditional visibility and requirement rules for form fields
 */

import {
  ConditionalAction,
  ConditionalOperator,
  type ConditionalRequirementSetting,
  type ConditionalVisibilitySetting,
} from '@sunrise-ui/api/insights';

import type { FieldState, FormValues } from '../types';

/**
 * Evaluates a single condition against the source field value
 */
export function evaluateCondition(
  operator: ConditionalOperator,
  sourceValue: unknown,
  conditionValue: unknown,
): boolean {
  switch (operator) {
    case ConditionalOperator.IsEmpty:
      return (
        sourceValue === null ||
        sourceValue === undefined ||
        sourceValue === '' ||
        (Array.isArray(sourceValue) && sourceValue.length === 0)
      );

    case ConditionalOperator.IsNotEmpty:
      return (
        sourceValue !== null &&
        sourceValue !== undefined &&
        sourceValue !== '' &&
        !(Array.isArray(sourceValue) && sourceValue.length === 0)
      );

    case ConditionalOperator.Equals:
      return sourceValue === conditionValue;

    case ConditionalOperator.NotEquals:
      return sourceValue !== conditionValue;

    case ConditionalOperator.Contains:
      if (typeof sourceValue === 'string') {
        return sourceValue.includes(String(conditionValue));
      }
      if (Array.isArray(sourceValue)) {
        return sourceValue.includes(conditionValue);
      }
      return false;

    case ConditionalOperator.NotContains:
      if (typeof sourceValue === 'string') {
        return !sourceValue.includes(String(conditionValue));
      }
      if (Array.isArray(sourceValue)) {
        return !sourceValue.includes(conditionValue);
      }
      return true;

    case ConditionalOperator.GreaterThan:
      return Number(sourceValue) > Number(conditionValue);

    case ConditionalOperator.LessThan:
      return Number(sourceValue) < Number(conditionValue);

    case ConditionalOperator.InList:
      return (
        Array.isArray(conditionValue) && conditionValue.includes(sourceValue)
      );

    case ConditionalOperator.NotInList:
      return (
        !Array.isArray(conditionValue) || !conditionValue.includes(sourceValue)
      );

    default:
      return false;
  }
}

/**
 * Evaluates visibility conditions for a field
 */
export function evaluateVisibility(
  visibilitySettings: ConditionalVisibilitySetting[] | undefined,
  formValues: FormValues,
  defaultVisible: boolean = true,
): boolean {
  if (!visibilitySettings || visibilitySettings.length === 0) {
    return defaultVisible;
  }

  // Evaluate each visibility condition
  for (const setting of visibilitySettings) {
    const sourceValue = formValues[setting.source_field_id];
    const conditionMet = evaluateCondition(
      setting.operator,
      sourceValue,
      setting.value,
    );

    // Apply action based on condition result
    if (conditionMet) {
      if (setting.action === ConditionalAction.Show) {
        return true;
      }
      if (setting.action === ConditionalAction.Hide) {
        return false;
      }
    }
  }

  return defaultVisible;
}

/**
 * Evaluates requirement conditions for a field
 */
export function evaluateRequirement(
  requirementSettings: ConditionalRequirementSetting[] | undefined,
  formValues: FormValues,
  defaultRequired: boolean = false,
): boolean {
  if (!requirementSettings || requirementSettings.length === 0) {
    return defaultRequired;
  }

  // Evaluate each requirement condition
  for (const setting of requirementSettings) {
    const sourceValue = formValues[setting.source_field_id];
    const conditionMet = evaluateCondition(
      setting.operator,
      sourceValue,
      setting.value,
    );

    // If condition is met, field becomes required
    if (conditionMet) {
      return true;
    }
  }

  return defaultRequired;
}

/**
 * Evaluates disabled state based on conditional settings
 */
export function evaluateDisabled(
  visibilitySettings: ConditionalVisibilitySetting[] | undefined,
  formValues: FormValues,
  defaultDisabled: boolean = false,
): boolean {
  if (!visibilitySettings || visibilitySettings.length === 0) {
    return defaultDisabled;
  }

  // Check for enable/disable actions in visibility settings
  for (const setting of visibilitySettings) {
    const sourceValue = formValues[setting.source_field_id];
    const conditionMet = evaluateCondition(
      setting.operator,
      sourceValue,
      setting.value,
    );

    if (conditionMet) {
      if (setting.action === ConditionalAction.Enable) {
        return false;
      }
      if (setting.action === ConditionalAction.Disable) {
        return true;
      }
    }
  }

  return defaultDisabled;
}

type FieldConditionalConfig = {
  visibilitySettings?: ConditionalVisibilitySetting[];
  requirementSettings?: ConditionalRequirementSetting[];
  defaultVisible?: boolean;
  defaultRequired?: boolean;
  defaultDisabled?: boolean;
};

/**
 * Evaluates all conditional states for a field
 */
export function evaluateFieldState(
  config: FieldConditionalConfig,
  formValues: FormValues,
): FieldState {
  const {
    visibilitySettings,
    requirementSettings,
    defaultVisible = true,
    defaultRequired = false,
    defaultDisabled = false,
  } = config;

  const visible = evaluateVisibility(
    visibilitySettings,
    formValues,
    defaultVisible,
  );
  const required = evaluateRequirement(
    requirementSettings,
    formValues,
    defaultRequired,
  );
  const disabled = evaluateDisabled(
    visibilitySettings,
    formValues,
    defaultDisabled,
  );

  return { visible, required, disabled };
}
